export const CANNED_WIDGET_DEFAULT_FONT_SIZE_PT = 18;
export const CANNED_WIDGET_MIN_FONT_SIZE_PT = 8;
export const CANNED_WIDGET_PADDING_PT = 16;

/**
 * Times Roman's ascent + descent is ~0.9em and Apryse centres glyphs vertically,
 * so a widget shorter than ~1.3× the font size clips "p", "g", "y", "j".
 */
export const CANNED_WIDGET_MIN_HEIGHT_TO_FONT_RATIO = 1.3;

export interface CannedWidgetAutoFitInputs {
  currentFontSizePt: number;
  hasMultilineFlag: boolean;
  pageWidthPt: number;
  widgetHeightPt: number;
  widgetWidthPt: number;
  widgetXPt: number;
  /** Text width in points measured at `currentFontSizePt`; the fn scales it linearly for other sizes. */
  widthAtCurrentFontSizePt: number;
}

export interface CannedWidgetAutoFitDecision {
  fontSizePt: number;
  hasMultilineFlag: boolean;
  widgetHeightPt: number;
  widgetWidthPt: number;
}

/**
 * Shrink font until it fits both the widget's width and height; only grow the
 * rect once the font has bottomed out at `CANNED_WIDGET_MIN_FONT_SIZE_PT`, so
 * the sender's placement is respected whenever the value can be scaled to it.
 * MULTILINE must stay off: Apryse's flatten writes wrapped lines past the
 * widget rect into neighbouring content — that was the original CS bug.
 */
export function computeCannedWidgetAutoFit(
  inputs: CannedWidgetAutoFitInputs
): CannedWidgetAutoFitDecision | null {
  const {
    hasMultilineFlag,
    pageWidthPt,
    widgetHeightPt,
    widgetWidthPt,
    widgetXPt,
    widthAtCurrentFontSizePt,
  } = inputs;
  // 0 is Apryse's auto-fit sentinel; avoid it to skip a 0/0 further down.
  const currentFontSizePt =
    inputs.currentFontSizePt > 0 ? inputs.currentFontSizePt : CANNED_WIDGET_DEFAULT_FONT_SIZE_PT;
  const usableWidthPt = widgetWidthPt - CANNED_WIDGET_PADDING_PT;

  const initialDecision: CannedWidgetAutoFitDecision = {
    fontSizePt: currentFontSizePt,
    hasMultilineFlag: false,
    widgetHeightPt,
    widgetWidthPt,
  };
  const noValueOrRoom = widthAtCurrentFontSizePt <= 0 || usableWidthPt <= 0;
  if (noValueOrRoom) return hasMultilineFlag ? initialDecision : null;

  // Largest font that would fit the current width, if the shrink math was
  // allowed to pick anything down to the floor.
  const widthLimitedFontSizePt = Math.floor(
    (currentFontSizePt * usableWidthPt) / widthAtCurrentFontSizePt
  );
  // Largest font whose 1.3× line box fits the current height.
  const heightLimitedFontSizePt = Math.floor(
    widgetHeightPt / CANNED_WIDGET_MIN_HEIGHT_TO_FONT_RATIO
  );
  const nextFontSizePt = Math.min(
    CANNED_WIDGET_DEFAULT_FONT_SIZE_PT,
    Math.max(
      CANNED_WIDGET_MIN_FONT_SIZE_PT,
      Math.min(widthLimitedFontSizePt, heightLimitedFontSizePt)
    )
  );

  // Font advance widths are linear in font size for a fixed font, so the width
  // at the new size is a scaling of the measurement caller handed in.
  const widthAtNextFontSizePt = (widthAtCurrentFontSizePt * nextFontSizePt) / currentFontSizePt;
  let nextWidgetWidthPt = widgetWidthPt;
  if (widthAtNextFontSizePt > usableWidthPt) {
    const maxWidgetWidthPt = pageWidthPt - widgetXPt;
    const neededWidgetWidthPt = Math.min(
      maxWidgetWidthPt,
      widthAtNextFontSizePt + CANNED_WIDGET_PADDING_PT
    );
    if (neededWidgetWidthPt > widgetWidthPt) nextWidgetWidthPt = neededWidgetWidthPt;
  }

  let nextWidgetHeightPt = widgetHeightPt;
  const minWidgetHeightPt = Math.ceil(nextFontSizePt * CANNED_WIDGET_MIN_HEIGHT_TO_FONT_RATIO);
  if (minWidgetHeightPt > widgetHeightPt) nextWidgetHeightPt = minWidgetHeightPt;

  const decision: CannedWidgetAutoFitDecision = {
    fontSizePt: nextFontSizePt,
    hasMultilineFlag: false,
    widgetHeightPt: nextWidgetHeightPt,
    widgetWidthPt: nextWidgetWidthPt,
  };
  const unchanged =
    !hasMultilineFlag &&
    decision.fontSizePt === inputs.currentFontSizePt &&
    decision.widgetWidthPt === widgetWidthPt &&
    decision.widgetHeightPt === widgetHeightPt;
  return unchanged ? null : decision;
}
